import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, SearchBar, Card, Table, Td, EmptyRow, Btn, Badge, CompanyInit, Modal, FormField, Input, Select, useToast, StatCard } from '../../components/ui';

export default function CompanyEmployees({ companyId, userRole }: { companyId: string; userRole?: string }) {
  const toast = useToast();
  const isManager = userRole === 'manager';
  const [employees, setEmployees] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState<'add' | 'edit' | 'assign' | 'success' | null>(null);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [createdUser, setCreatedUser] = useState<any>(null);
  const [form, setForm] = useState({ full_name: '', email: '', employee_code: '', phone: '', role: 'employee', is_geofenced: 'true', kiosk_only: 'false' });
  const [assignForm, setAssignForm] = useState({ location_id: '', shift_id: '' });

  const load = useCallback(async () => {
    const [{ data: emps }, { data: locs }, { data: shfts }] = await Promise.all([
      sb.from('users').select('*, location:locations(name), shift:shifts(name, start_time, end_time), salary_structures(basic_salary)').eq('company_id', companyId).in('role', isManager ? ['employee','field_executive'] : ['employee','field_executive','manager','company_admin']).order('full_name'),
      sb.from('locations').select('*').eq('company_id', companyId),
      sb.from('shifts').select('*').eq('company_id', companyId),
    ]);
    setEmployees(emps || []); setLocations(locs || []); setShifts(shfts || []);
  }, [companyId, isManager]);

  useEffect(() => { load(); }, [load]);

  const filtered = employees.filter(e => e.full_name?.toLowerCase().includes(query.toLowerCase()) || e.email?.toLowerCase().includes(query.toLowerCase()) || e.employee_code?.toLowerCase().includes(query.toLowerCase()));

  const createEmployee = async () => {
    if (!form.full_name) { toast('Name is required', 'error'); return; }
    if (form.kiosk_only === 'false' && !form.email) { toast('Email is required for mobile users', 'error'); return; }
    setSaving(true);
    try {
      if (form.kiosk_only === 'true') {
        const { error } = await sb.from('users').insert({ full_name: form.full_name, email: null, role: isManager ? 'employee' : form.role, company_id: companyId, employee_code: form.employee_code || null, phone: form.phone || null, is_geofenced: form.is_geofenced === 'true', kiosk_only: true, is_active: true, face_enrolled: false });
        if (error) throw error;
        setCreatedUser({ full_name: form.full_name, email: 'kiosk-only', temp_password: '' });
      } else {
        const { data: { session } } = await sb.auth.getSession();
        const res = await fetch('https://quxasqjlhohghnuurvvl.supabase.co/functions/v1/create-user', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ full_name: form.full_name, email: form.email, role: isManager ? 'employee' : form.role, company_id: companyId, employee_code: form.employee_code, phone: form.phone, is_geofenced: form.is_geofenced === 'true', kiosk_only: false }),
        });
        const result = await res.json();
        if (!result.success) throw new Error(result.error);
        setCreatedUser({ full_name: form.full_name, email: result.email, temp_password: result.temp_password });
      }
      toast(`✅ ${form.full_name} added!`, 'success'); setModal('success'); load();
    } catch (e: any) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const upgradeToMobile = async () => {
    if (!editing || !upgradeEmail) { toast('Email required', 'error'); return; }
    setUpgrading(true);
    try {
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch('https://quxasqjlhohghnuurvvl.supabase.co/functions/v1/create-user', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ full_name: editing.full_name, email: upgradeEmail, role: editing.role, company_id: companyId, employee_code: editing.employee_code, kiosk_only: false, existing_user_id: editing.id }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error);
      await sb.from('users').update({ email: upgradeEmail, kiosk_only: false }).eq('id', editing.id);
      setCreatedUser({ full_name: editing.full_name, email: upgradeEmail, temp_password: result.temp_password });
      setModal('success'); setShowUpgrade(false); load();
    } catch (e: any) { toast(e.message, 'error'); }
    finally { setUpgrading(false); }
  };

  const saveAssign = async () => {
    if (!editing) return;
    setSaving(true);
    await sb.from('users').update({ location_id: assignForm.location_id || null, shift_id: assignForm.shift_id || null }).eq('id', editing.id);
    setSaving(false); toast('✅ Assigned!', 'success'); setModal(null); load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    await sb.from('users').update({ full_name: form.full_name, role: form.role, employee_code: form.employee_code, phone: form.phone, is_geofenced: form.is_geofenced === 'true' }).eq('id', editing.id);
    setSaving(false); toast('Saved!', 'success'); setModal(null); load();
  };

  const toggleActive = async (emp: any) => {
    await sb.from('users').update({ is_active: !emp.is_active }).eq('id', emp.id);
    toast(emp.is_active ? 'Deactivated' : 'Activated', 'success'); load();
  };

  const withLocation = employees.filter(e => e.location_id).length;
  const withSalary = employees.filter(e => e.salary_structures?.length > 0).length;

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title={isManager ? 'My Team' : 'Employees'} subtitle={`${employees.length} employees`} action={<Btn onClick={() => { setForm({ full_name: '', email: '', employee_code: '', phone: '', role: 'employee', is_geofenced: 'true', kiosk_only: 'false' }); setEditing(null); setModal('add'); }}>+ Add Employee</Btn>} />

      {!isManager && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
          <StatCard label="Location Assigned" value={`${withLocation} / ${employees.length}`} color="var(--accent)" />
          <StatCard label="Salary Configured" value={`${withSalary} / ${employees.length}`} color="#845EC2" />
          <StatCard label="Needs Setup" value={employees.filter(e => e.is_geofenced && !e.location_id).length} color="var(--warn)" />
        </div>
      )}

      <SearchBar placeholder="Search by name, email or code..." onSearch={setQuery} />
      <Card>
        <Table headers={isManager ? ['Employee','Code','Phone','Face','Actions'] : ['Employee','Code','Role','Location','Shift','Face','Type','Actions']}>
          {filtered.length === 0 ? <EmptyRow cols={isManager ? 5 : 8} message="No employees yet" /> :
           filtered.map(emp => (
            <tr key={emp.id}>
              <Td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CompanyInit name={emp.full_name || '?'} size={32} />
                  <div><div style={{ fontWeight: 600 }}>{emp.full_name}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>{emp.email || 'No email'}</div></div>
                </div>
              </Td>
              <Td mono>{emp.employee_code || '—'}</Td>
              {!isManager && <Td><Badge type="purple">{emp.role}</Badge></Td>}
              {!isManager && <Td>{emp.location ? <span style={{ color: 'var(--accent)', fontSize: 12 }}>📍 {emp.location.name}</span> : emp.is_geofenced ? <span style={{ color: 'var(--warn)', fontSize: 12 }}>⚠️ Not set</span> : <span style={{ color: 'var(--muted)', fontSize: 12 }}>Field</span>}</Td>}
              {!isManager && <Td>{emp.shift ? <span style={{ fontSize: 12 }}>🕐 {emp.shift.name}</span> : <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>}</Td>}
              {isManager && <Td mono>{emp.phone || '—'}</Td>}
              <Td>{emp.face_enrolled ? '✅' : <span style={{ color: 'var(--danger)', fontSize: 12 }}>Not enrolled</span>}</Td>
              {!isManager && <Td><Badge type={emp.kiosk_only ? 'purple' : 'green'}>{emp.kiosk_only ? '📟 Kiosk' : '📱 Mobile'}</Badge></Td>}
              <Td>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {!isManager && <Btn variant="accent" size="sm" onClick={() => { setEditing(emp); setAssignForm({ location_id: emp.location_id || '', shift_id: emp.shift_id || '' }); setModal('assign'); }}>📍 Assign</Btn>}
                  <Btn variant="outline" size="sm" onClick={() => { setForm({ full_name: emp.full_name, email: emp.email || '', employee_code: emp.employee_code || '', phone: emp.phone || '', role: emp.role, is_geofenced: String(emp.is_geofenced), kiosk_only: String(emp.kiosk_only || false) }); setEditing(emp); setShowUpgrade(false); setUpgradeEmail(''); setModal('edit'); }}>Edit</Btn>
                  {!isManager && <Btn variant="danger" size="sm" onClick={() => toggleActive(emp)}>{emp.is_active ? 'Deactivate' : 'Activate'}</Btn>}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {/* Assign Modal */}
      {modal === 'assign' && editing && (
        <Modal title={`Assign — ${editing.full_name}`} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gap: 16 }}>
            <FormField label="Check-in Location">
              <Select value={assignForm.location_id} onChange={e => setAssignForm(f => ({...f, location_id: e.target.value}))}>
                <option value="">— No location (field executive) —</option>
                {locations.map(l => <option key={l.id} value={l.id}>📍 {l.name} ({l.radius_meters}m)</option>)}
              </Select>
            </FormField>
            <FormField label="Work Shift (optional)">
              <Select value={assignForm.shift_id} onChange={e => setAssignForm(f => ({...f, shift_id: e.target.value}))}>
                <option value="">— No shift —</option>
                {shifts.map(s => <option key={s.id} value={s.id}>🕐 {s.name} ({s.start_time}–{s.end_time})</option>)}
              </Select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={saveAssign} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {modal === 'add' && (
        <Modal title="Add Employee" onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><FormField label="Full Name"><Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} placeholder="John Doe" /></FormField></div>
            <div style={{ gridColumn: '1/-1' }}>
              <FormField label="Employee Type">
                <Select value={form.kiosk_only} onChange={e => setForm(f => ({...f, kiosk_only: e.target.value}))}>
                  <option value="false">📱 Mobile App User (needs email)</option>
                  <option value="true">📟 Kiosk Only (no email, enroll face on tablet)</option>
                </Select>
              </FormField>
            </div>
            {form.kiosk_only === 'false' && <div style={{ gridColumn: '1/-1' }}><FormField label="Email"><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="john@company.com" /></FormField></div>}
            <FormField label="Employee Code"><Input value={form.employee_code} onChange={e => setForm(f => ({...f, employee_code: e.target.value}))} placeholder="EMP001" /></FormField>
            <FormField label="Phone"><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+91 9876543210" /></FormField>
            {!isManager && (
              <FormField label="Role">
                <Select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
                  <option value="employee">Employee</option><option value="field_executive">Field Executive</option>
                  <option value="manager">Manager</option><option value="company_admin">Company Admin</option>
                </Select>
              </FormField>
            )}
            <FormField label="Geofenced Check-in">
              <Select value={form.is_geofenced} onChange={e => setForm(f => ({...f, is_geofenced: e.target.value}))}>
                <option value="true">Yes — must be at office</option><option value="false">No — check in from anywhere</option>
              </Select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={createEmployee} disabled={saving}>{saving ? 'Creating...' : 'Create Employee'}</Btn>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && editing && (
        <Modal title={`Edit — ${editing.full_name}`} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><FormField label="Full Name"><Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} /></FormField></div>
            {!isManager && <FormField label="Role"><Select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}><option value="employee">Employee</option><option value="field_executive">Field Executive</option><option value="manager">Manager</option><option value="company_admin">Company Admin</option></Select></FormField>}
            <FormField label="Employee Code"><Input value={form.employee_code} onChange={e => setForm(f => ({...f, employee_code: e.target.value}))} /></FormField>
            <FormField label="Phone"><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></FormField>
          </div>

          {editing.kiosk_only && !isManager && (
            <div style={{ marginTop: 16, background: '#845EC210', border: '1px solid #845EC230', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#845EC2', marginBottom: 8 }}>📱 Upgrade to Mobile App</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>Give this employee mobile app access. Face enrollment carries over — no re-enrollment needed!</div>
              {!showUpgrade ? <Btn variant="outline" onClick={() => setShowUpgrade(true)}>+ Add Email & Enable App Access</Btn> : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input value={upgradeEmail} onChange={e => setUpgradeEmail(e.target.value)} placeholder="employee@company.com" style={{ flex: 1 }} />
                  <Btn onClick={upgradeToMobile} disabled={upgrading}>{upgrading ? 'Upgrading...' : '✅ Upgrade'}</Btn>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Btn>
          </div>
        </Modal>
      )}

      {/* Success Modal */}
      {modal === 'success' && createdUser && (
        <Modal title="✅ Employee Created!" onClose={() => { setModal(null); setCreatedUser(null); }}>
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{createdUser.full_name}</div>
          </div>
          {createdUser.email === 'kiosk-only' ? (
            <div style={{ background: '#845EC210', border: '1px solid #845EC230', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#845EC2', fontWeight: 700, marginBottom: 8 }}>📟 Kiosk-Only Employee</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>Go to the kiosk tablet → tap "+ Enroll New Employee" → complete face enrollment.</div>
            </div>
          ) : (
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>Email</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', fontSize: 13 }}>{createdUser.email}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>Temp Password</span>
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--warn)', background: '#F9A82515', padding: '4px 10px', borderRadius: 6 }}>{createdUser.temp_password}</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="outline" onClick={() => { setModal('add'); setCreatedUser(null); setForm({ full_name: '', email: '', employee_code: '', phone: '', role: 'employee', is_geofenced: 'true', kiosk_only: 'false' }); }}>+ Add Another</Btn>
            <Btn onClick={() => { setModal(null); setCreatedUser(null); }}>Done</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
